import { PushProcessingNotification } from "@repo/helpers";
import { Button, ButtonColor, DropDownList, FiltersV2, TextBox } from "@repo/ui";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useCallback, useState } from "react";

import type { FieldAuditsFilter } from "./types";

import FieldAuditsApi from "../../api/fieldAuditsApi";
import { QUERY_KEYS } from "../../api/queryKeys";
import { emptySegmentFilters, SegmentFilters } from "../../components/segmentFilters";
import { FieldAuditsGrid } from "./grid";

const initialFilters: FieldAuditsFilter = {
	currentPage: 1,
	itemsPerPage: 25,
	repLogin: undefined,
	auditTypeId: undefined,
	locationId: undefined,
	evaluationId: undefined,
	brandIds: emptySegmentFilters.brandIds,
	channelIds: emptySegmentFilters.channelIds,
	marketerIds: emptySegmentFilters.marketerIds,
	stateIds: emptySegmentFilters.stateIds,
	branchIds: emptySegmentFilters.branchIds,
};

export function FieldAuditsPage() {
	const queryClient = useQueryClient();
	const [filters, setFilters] = useState<FieldAuditsFilter>(initialFilters);
	const [executedFilters, setExecutedFilters] = useState<FieldAuditsFilter>(initialFilters);
	const [importUrl, setImportUrl] = useState("");
	const [submitting, setSubmitting] = useState(false);

	const auditTypesQuery = useQuery({
		queryKey: ["fieldAuditTypes"],
		queryFn: () => FieldAuditsApi.getFieldAuditTypes(),
	});

	const locationsQuery = useQuery({
		queryKey: [QUERY_KEYS.FieldAudits.Locations],
		queryFn: () => FieldAuditsApi.getLocations(),
	});

	const handleImport = useCallback(async () => {
		if (!importUrl) return;

		setSubmitting(true);
		const notify = PushProcessingNotification("Importing Secret Shopper Audit...");

		try {
			await FieldAuditsApi.importFieldAudit(importUrl);
			notify.success("Secret Shopper Audit imported successfully.");
			setImportUrl("");
			await queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.FieldAudits.Grid] });
		} catch (error) {
			notify.error(error?.message ?? "Failed to import Secret Shopper Audit.");
		} finally {
			setSubmitting(false);
		}
	}, [importUrl, queryClient]);

	const filterChanged = useCallback(
		(value: any, name: string) => {
			setFilters((prev) => ({ ...prev, [name]: value }));
		},
		[setFilters],
	);

	const segmentFiltersChanged = useCallback(
		(s) =>
			setFilters((prev) => ({
				...prev,
				brands: s.brandIds,
				channels: s.channelIds,
				marketers: s.marketerIds,
				states: s.stateIds,
				branches: s.branchIds,
			})),
		[setFilters],
	);

	return (
		<div className="w-full">
			<FiltersV2
				onApply={() =>
					setExecutedFilters((currentFilters) => ({
						...filters,
						currentPage: 1,
						itemsPerPage: currentFilters.itemsPerPage,
					}))
				}
			>
				<SegmentFilters onChange={segmentFiltersChanged} />

				<TextBox
					id="repLogin"
					label="Agent Id"
					value={filters.repLogin ?? ""}
					changeHandler={(v) => filterChanged(v, "repLogin")}
				/>
				<DropDownList
					id="auditTypeId"
					label="Audit Type"
					valueProp="Value"
					textProp="Text"
					value={filters.auditTypeId ?? ""}
					values={auditTypesQuery.data ?? []}
					changeHandler={(v) => filterChanged(parseInt(v), "auditTypeId")}
					fullLength
				/>
				<DropDownList
					id="locationId"
					label="Location"
					valueProp="LocationId"
					textProp="Name"
					value={filters.locationId ?? ""}
					values={locationsQuery.data ?? []}
					changeHandler={(v) => filterChanged(parseInt(v), "locationId")}
					fullLength
				/>
				<TextBox
					id="evaluationId"
					label="Evaluation Id"
					value={filters.evaluationId ?? ""}
					changeHandler={(v) => filterChanged(v, "evaluationId")}
				/>
			</FiltersV2>
			<div className="my-4 flex flex-col gap-2 rounded-lg bg-[#f3f3f4] p-4 shadow-sm">
				<div className="flex items-end justify-center gap-2">
					<TextBox
						id="importUrl"
						label="Import Secret Shopper Audit"
						type="url"
						placeholder="Secret Shopper Url"
						value={importUrl}
						changeHandler={(v) => setImportUrl(v)}
						className="!m-0 flex-grow"
					/>
					<Button
						color={ButtonColor.Green}
						onClick={handleImport}
						disabled={!importUrl || submitting}
						loading={submitting}
					>
						Import
					</Button>
				</div>
			</div>

			<FieldAuditsGrid filters={executedFilters} setFilters={setExecutedFilters} />
		</div>
	);
}
